/**
 * LetterDetailView -- a regulator letter + its observations, with a
 * one-click "Create CAP from this letter" (P265 click-path).
 *
 * The reactive flow's missing surface: letter intake created a letter but
 * there was no way to view it or turn it into a CAP without manual hops.
 * Mounted at /framework/letters/:letterId; linked from the CAP detail
 * letter card + the intake success banner.
 */
import { RegulatorTag } from "shared/components/RegulatorTag";
import { formatDate } from "shared/time/formatDate";
import { ThreadStrip } from "shared/thread/ThreadStrip";
import { letterSourceLabel, stateLabel } from "shared/vocab/workflowState";
import { i18n } from "shared/i18n";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "shared/api/client";
import { useTranslation } from "react-i18next";

import { useState } from "react";

import {
  useAddObservation,
  useCreateCap,
  useDeleteObservation,
  useLetter,
} from "framework/api/hooks";
import type { Severity } from "shared/api/types";
import { EngineOffBanner } from "shared/components/EngineOffBanner";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { hasPosition, useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";

import { Arrow } from "shared/components/Arrow";
const SEVERITY_TONE: Record<string, string> = {
  critical: "bg-rose-100 text-rose-800",
  high: "bg-amber-100 text-amber-800",
  medium: "bg-sky-100 text-sky-800",
  low: "bg-slate-100 text-slate-700",
};

export function LetterDetailView() {
  const { t } = useTranslation();
  const { letterId } = useParams<{ letterId: string }>();
  const me = useMe();
  const toast = useToast();
  const navigate = useNavigate();
  const letter = useLetter(letterId);
  const createCap = useCreateCap();
  const addObs = useAddObservation(letterId ?? "");
  const removeObs = useDeleteObservation(letterId ?? "");
  // F -- the CAP was named for the operator, every time.
  const [capTitle, setCapTitle] = useState("");
  const [obsTitle, setObsTitle] = useState("");
  const [obsBody, setObsBody] = useState("");
  const [obsSeverity, setObsSeverity] = useState<Severity>("medium");

  const canCreate = me.data?.is_admin === true || hasPosition(me.data, "preparer");
  // Writing the letter's observations is the preparer's job -- the same
  // gate the backend applies.
  const canEdit = canCreate;

  if (letter.isError) {
    return (
      <div data-testid="letter-detail-not-found" className="text-sm text-slate-500">
        {t("letters.detail.notFound")}
      </div>
    );
  }
  if (!letter.data) {
    return <div className="text-sm text-slate-500">…</div>;
  }
  const l = letter.data;
  const defaultCapTitle = `Remediation -- ${l.title}`;
  const existingCaps = l.caps?.length ?? 0;

  function onCreateCap() {
    if (!l) return;
    createCap.mutate(
      // F -- the CAP was named for the operator: "Remediation -- " and
      // the letter's own title, every time. A plan the bank will work
      // for months, and the person raising it could not say what it
      // was for. The composed name stays as the default, because it is
      // a reasonable one and nobody should be blocked on naming.
      { letter_id: l.id, title: capTitle.trim() || defaultCapTitle },
      {
        onSuccess: (cap) => {
          toast.push({ tone: "success", message: t("letters.detail.created") });
          navigate(`/framework/caps/${cap.id}`);
        },
        onError: (e) => toast.push({ tone: "error", message: e.message }),
      },
    );
  }

  return (
    <article data-testid="letter-detail-view" className="space-y-5">
      <Link
        to="/framework"
        className="text-xs text-brand-brassDark hover:text-brand-brass"
      >
        <Arrow kind="back" /> {t("letters.detail.back")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div>
            <RegulatorTag
              code={l.regulator_code}
              className="text-xs font-mono text-slate-500"
            />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">
            <MarsadIcon
              name="obligation"
              size={20}
              className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
            />
            {l.title}
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            {t("letters.detail.received")}: {formatDate(l.received_at, i18n.language)} ·{" "}
            {letterSourceLabel(l.source_kind)}
          </p>
        </div>
        {canCreate && (
          <div className="flex flex-col items-end gap-1">
            {/* F -- a letter that already has a plan against it was
                offered a bare "Create CAP", so the regulatory-change
                flow's own anchor invited a SECOND plan for the same
                circular. Raising another is legitimate -- one letter
                can need several -- so this says what exists rather
                than refusing. */}
            {existingCaps > 0 && (
              <span
                data-testid="letter-existing-caps"
                className="text-[11px] text-amber-800"
              >
                {t("letters.detail.alreadyHasCaps", { count: existingCaps })}
              </span>
            )}
            <input
              dir="auto"
              data-testid="letter-cap-title"
              value={capTitle}
              onChange={(e) => setCapTitle(e.target.value)}
              placeholder={defaultCapTitle}
              aria-label={t("letters.detail.capTitleLabel")}
              className="w-72 rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <button
              type="button"
              data-testid="letter-create-cap"
              onClick={onCreateCap}
              disabled={createCap.isPending}
              className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1.5 text-sm font-semibold text-brand-navy disabled:opacity-50"
            >
              {createCap.isPending
                ? t("letters.detail.creating")
                : existingCaps > 0
                  ? t("letters.detail.createAnotherCap")
                  : t("letters.detail.createCap")}
            </button>
          </div>
        )}
      </header>
      {/* Stitching review 5.2 -- the letter's thread: the asks it makes,
          what the plans answering it rest on, how many asks have an
          action, whose seat each plan waits in, and what is sealed. */}
      <ThreadStrip subject="letter" id={letterId} />

      <section>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t("letters.detail.observations")}
        </h2>
        {/* C9 -- paste mode can only infer ONE observation, so a letter
            raising four requirements filed its whole response against
            one undifferentiated entry. Splitting it is a person's
            judgement; this is where they record it. */}
        <EngineOffBanner manualKey="engineOff.letter" />
        {l.observations.length === 0 ? (
          <p className="text-sm text-slate-500">{t("letters.detail.noObservations")}</p>
        ) : (
          <ul className="space-y-2">
            {l.observations.map((o) => (
              <li
                key={o.id}
                data-testid="letter-observation"
                className="rounded border border-slate-200 bg-white p-3"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-[11px] text-slate-400">
                    #{o.sequence}
                  </span>
                  <span className="text-sm font-medium text-slate-900">{o.title}</span>
                  <span
                    className={`ms-auto rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      SEVERITY_TONE[o.severity] ?? SEVERITY_TONE.low
                    }`}
                  >
                    {o.severity}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-600">{o.body}</p>
                {canEdit && (
                  <button
                    type="button"
                    data-testid={`letter-observation-remove-${o.id}`}
                    onClick={() =>
                      removeObs.mutate(o.id, {
                        onSuccess: () =>
                          toast.push({
                            tone: "success",
                            message: t("letters.detail.obsRemoved"),
                          }),
                        // The 409 says how many action items are filed
                        // against it -- the one thing the operator
                        // needs in order to act.
                        onError: (e) =>
                          toast.push({ tone: "error", message: e.detail ?? "error" }),
                      })
                    }
                    className="mt-1 text-[11px] text-rose-700 hover:underline"
                  >
                    {t("letters.detail.obsRemove")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <form
            data-testid="letter-add-observation"
            className="mt-3 space-y-2 rounded border border-slate-200 bg-slate-50 p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!obsTitle.trim() || !obsBody.trim()) return;
              addObs.mutate(
                {
                  title: obsTitle.trim(),
                  body: obsBody.trim(),
                  severity: obsSeverity,
                },
                {
                  onSuccess: () => {
                    setObsTitle("");
                    setObsBody("");
                    toast.push({
                      tone: "success",
                      message: t("letters.detail.obsAdded"),
                    });
                  },
                  onError: (e2) =>
                    toast.push({ tone: "error", message: e2.detail ?? "error" }),
                },
              );
            }}
          >
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {t("letters.detail.addObservation")}
            </p>
            <input
              dir="auto"
              data-testid="letter-obs-title"
              value={obsTitle}
              onChange={(e) => setObsTitle(e.target.value)}
              placeholder={t("letters.detail.obsTitlePlaceholder")}
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <textarea
              dir="auto"
              data-testid="letter-obs-body"
              value={obsBody}
              onChange={(e) => setObsBody(e.target.value)}
              placeholder={t("letters.detail.obsBodyPlaceholder")}
              rows={3}
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <div className="flex items-center gap-2">
              <select
                data-testid="letter-obs-severity"
                value={obsSeverity}
                onChange={(e) => setObsSeverity(e.target.value as Severity)}
                className="rounded border border-slate-300 px-2 py-1 text-xs"
              >
                {(["low", "medium", "high", "critical"] as const).map((sv) => (
                  <option key={sv} value={sv}>
                    {sv}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                data-testid="letter-obs-submit"
                disabled={!obsTitle.trim() || !obsBody.trim() || addObs.isPending}
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
              >
                {t("letters.detail.obsAdd")}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* P272.3 (UX-2 J3-5b): the letter shows its CAPs + closure -- the
          walk found the letter never reflected whether it was remediated. */}
      <section data-testid="letter-caps">
        <h2 className="mb-2 flex items-baseline gap-2 text-sm font-semibold text-slate-700">
          {t("letters.detail.capsHeading")}
          {(l.caps?.length ?? 0) > 0 && (
            <span
              data-testid="letter-caps-rollup"
              className="text-xs font-normal text-slate-500"
            >
              {t("letters.detail.capRollup", {
                approved: (l.caps ?? []).filter((c) => c.state === "approved").length,
                total: l.caps?.length ?? 0,
              })}
            </span>
          )}
        </h2>
        {(l.caps?.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">{t("letters.detail.noCaps")}</p>
        ) : (
          <ul className="space-y-2">
            {(l.caps ?? []).map((c) => (
              <li
                key={c.id}
                data-testid="letter-cap-row"
                className="flex items-baseline gap-2 rounded border border-slate-200 bg-white p-3"
              >
                <Link
                  to={`/framework/caps/${c.id}`}
                  data-testid={`letter-cap-link-${c.id}`}
                  className="text-sm font-medium text-brand-navy hover:text-brand-brass"
                >
                  {c.title}
                </Link>
                <span
                  data-testid="letter-cap-state"
                  data-state={c.state}
                  className={`ms-auto rounded px-1.5 py-0.5 text-[11px] font-medium ${
                    c.state === "approved"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-900"
                  }`}
                >
                  {/* Round 9 E8 -- `in_review` on the letter page, one
                      screen after the assessment drawer stopped
                      printing one. stateLabel is the vocabulary that
                      fix used. */}
                  {stateLabel(c.state)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
      {/* Round 18 T10 -- the attestations on what this letter's plans reach. */}
      <AttestationsOnLetter letterId={l.id} />
    </article>
  );
}

interface AttestationOnLetter {
  attestation_id: string;
  cycle_id: string;
  cycle_name: string;
  ibs_service_id: string;
  ibs_name: string;
  status: string;
}

/** Round 18 T10 -- the attestations on the services this letter's plans
 *  reach, through the findings they answer. Silent when none: a letter
 *  whose plans answer no finding on an attested service has nothing to
 *  name here. */
export function AttestationsOnLetter({ letterId }: { letterId: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["attestation-links", "letter", letterId],
    queryFn: () =>
      api.get<AttestationOnLetter[]>(
        `/v1/attestation-links/letter/${letterId}/attestations`,
      ),
  });
  const rows = Array.isArray(q.data) ? q.data : [];
  /* Round 19 G41 -- silent when none, absence and "not offered" read
     alike. Said, once the read has answered. */
  if (rows.length === 0) {
    if (!q.isSuccess) return null;
    return (
      <section data-testid="letter-attestations">
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t("letters.detail.attestationsHeading")}
        </h2>
        <p data-testid="letter-attestations-none" className="text-sm text-slate-600">
          {t("letters.detail.attestationsNone")}
        </p>
      </section>
    );
  }
  return (
    <section data-testid="letter-attestations">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">
        {t("letters.detail.attestationsHeading")}
      </h2>
      <ul className="space-y-1 text-sm">
        {rows.map((r) => (
          <li key={r.attestation_id} data-testid="letter-attestation-row">
            <Link
              to={`/framework/attestations?cycle=${r.cycle_id}&attestation=${r.attestation_id}`}
              className="text-brand-brassDark hover:underline"
            >
              <bdi>{r.ibs_name}</bdi>
            </Link>{" "}
            <span className="text-slate-600">
              {t("letters.detail.attestationIn", {
                cycle: r.cycle_name,
                status: t(`modules.framework.attestations.status.${r.status}`),
              })}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
